import { useQuery } from '@apollo/client';
import { IRecuperarEntregadoresEscaladosHoje } from 'components/infoPlayer';
import { format, parse } from 'date-fns';
import {
    COLETA_E_ENTREGADORES_ESCALADOS,
} from 'gql/coleta';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSubmitEditarStatus } from './useSubmitEditarStatus';

interface IStatusColeta {
    ativo: boolean;
    corBackground: string;
    corForeground: string;
    dataAlteracao: string;
    entregadorId?: string | null;
    id: number;
    statusId: number;
    nome: string;
    nomeCompleto: string | null;
    qtdxchamouplayer: number;
}
interface IOption {
    value: string;
    label: string;
}

const allStatus = [
    { id: 8, nome: 'Salvo' },
    { id: 1, nome: 'Chamando entregador' },
    { id: 10, nome: 'Visualizado' },
    { id: 11, nome: 'Recebido' },
    { id: 2, nome: 'Aceito' },
    { id: 3, nome: 'Checkin Unidade' },
    { id: 4, nome: 'Checkout Unidade' },
    { id: 5, nome: 'Checkin Cliente' },
    { id: 12, nome: 'Retorno ao Estabelecimento' },
    { id: 6, nome: 'Concluido' },
];

const dataParseSchema = 'yyyy-MM-dd HH:mm:ss';
const statusEmpty: IStatusColeta = {
    dataAlteracao: format(new Date(), dataParseSchema),
    nome: 'Chamando entregador',
    nomeCompleto: null,
    entregadorId: null,
    qtdxchamouplayer: 0,
    statusId: 1,
    corBackground: '#FF0000',
    corForeground: '#FFFFFF',
    id: 0,
    ativo: true,
};

export const useQueryData = () => {
    const { coletaId } = useParams<{ coletaId: string }>();
    const { data, loading } = useQuery<{
        recuperarEntregadoresPeriodoVigente?: IRecuperarEntregadoresEscaladosHoje[];
        statusColeta?: IStatusColeta[];
    }>(COLETA_E_ENTREGADORES_ESCALADOS, {
        variables: { coletaId },
        fetchPolicy: 'cache-first',
        skip: !coletaId,
    });

    const dateCollecttRef = useRef(new Date());
    const [optionSelected, setOptionSelected] = useState<IOption | null>(null);
    const [optionSelectedStatus, setOptionSelectedStatus] = useState<IOption | null>(null);

    // Entregadores escalados + entregador do último status
    const optionsPlayer = useMemo((): IOption[] => {
        if (data?.recuperarEntregadoresPeriodoVigente && data.statusColeta?.[0]) {
            const selected = data.statusColeta[0];

            const list = [
                ...data.recuperarEntregadoresPeriodoVigente,
                ...(selected.entregadorId
                    ? [{
                        entregadorId: selected.entregadorId,
                        entregadorNomeCompleto: selected.nomeCompleto ?? '',
                        entregadorTelefone: '',
                        entregadorLatitude: 0,
                        entregadorLongitude: 0,
                        entregadorDataGeo: '',
                        entregadorOnline: false,
                    }]
                    : []),
            ];

            const result = list.map((item) => ({
                value: item.entregadorId,
                label: item.entregadorNomeCompleto,
            }));

            return [...new Map(result.map((m) => [m.value, m])).values()];
        }
        return [];
    }, [data]);

    // Status únicos (apenas o mais recente de cada statusId)
    const statusUnique = useMemo(() => {
        if (!data?.statusColeta) return [statusEmpty];

        const map = new Map<number, IStatusColeta>();

        data.statusColeta
            .slice()
            .sort((a, b) => new Date(b.dataAlteracao).getTime() - new Date(a.dataAlteracao).getTime())
            .forEach((item) => {
                if (!map.has(item.statusId) && item.ativo && allStatus.some(s => s.id === item.statusId)) {
                    map.set(item.statusId, item);
                }
            });

        return Array.from(map.values());
    }, [data]);

    // Selecionar entregador automaticamente (último status)
    useEffect(() => {
        if (optionsPlayer.length > 0 && data?.statusColeta?.length) {
            const lastedStatus = [...data.statusColeta].sort(
                (a, b) => new Date(b.dataAlteracao).getTime() - new Date(a.dataAlteracao).getTime()
            )[0];

            if (lastedStatus?.entregadorId) {
                const statusLasted = optionsPlayer.find(item => item.value === lastedStatus.entregadorId);
                if (statusLasted) {
                    setOptionSelected(statusLasted);
                    return;
                }
            }
            setOptionSelected(optionsPlayer[0]);
        }
    }, [optionsPlayer, data]);

    // Selecionar status automaticamente (último status)
    useEffect(() => {
        if (data?.statusColeta?.length) {
            const lastedStatus = [...data.statusColeta].sort(
                (a, b) => new Date(b.dataAlteracao).getTime() - new Date(a.dataAlteracao).getTime()
            )[0];

            if (lastedStatus?.statusId) {
                const statusLasted = allStatus.find(item => item.id === lastedStatus.statusId);
                if (statusLasted) {
                    setOptionSelectedStatus({
                        value: String(statusLasted.id),
                        label: statusLasted.nome,
                    });
                }
            }
        }
    }, [data]);

    const handleChangeSelect = useCallback((e: IOption) => {
        if (e?.value) {
            setOptionSelected(e);
        }
    }, []);

    const handleChangeSelectStatus = useCallback((e: IOption) => {
        setOptionSelectedStatus(e);
    }, []);

    // Monta timeline dos status
    const response = useMemo(() => {
        if (!optionSelected || statusUnique.length === 0) {
            return [];
        }
        const firstStatus = statusUnique[statusUnique.length - 1];

        let horaStatus = parse(
            firstStatus.dataAlteracao.substring(0, 19),
            dataParseSchema,
            new Date()
        );

        dateCollecttRef.current = new Date(horaStatus);

        return allStatus.map((itemMap) => {
            const itemSelected = statusUnique.find((c) => c.statusId === itemMap.id);

            if (itemSelected?.ativo) {
                horaStatus = parse(
                    itemSelected.dataAlteracao.substring(0, 19),
                    dataParseSchema,
                    new Date()
                );
            } else {
                horaStatus.setSeconds(horaStatus.getSeconds() + 1);
            }

            const cloneDate = new Date(horaStatus);

            return {
                id: itemMap.id,
                nome: itemMap.nome,
                data: cloneDate,
                horaAlteracao: format(cloneDate, 'HH:mm:ss'),
                entregador: optionSelected.label,
            };
        });
    }, [statusUnique, optionSelected]);

    const dataSubmit = useSubmitEditarStatus({
        firstDate: dateCollecttRef.current,
        coletaId,
    });

    useEffect(() => {
        if (optionSelected && dataSubmit.ref.current) {
            dataSubmit.ref.current.setFieldValue('entregador', optionSelected);
        }
    }, [dataSubmit, optionSelected]);

    useEffect(() => {
        if (optionSelectedStatus && dataSubmit.ref.current) {
            dataSubmit.ref.current.setFieldValue('status', optionSelectedStatus);
        }
    }, [dataSubmit, optionSelectedStatus]);

    return {
        loading,
        optionsPlayer,
        optionSelected,
        optionSelectedStatus,
        handleChangeSelect,
        handleChangeSelectStatus,
        allStatus: response,
        ...dataSubmit,
    };
};
